import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const questions = [
  {
    question: "Décris ton activité en quelques mots.",
    placeholder: "Ex: Je propose du coaching en stratégie marketing pour entrepreneurs...",
    type: "textarea"
  },
  {
    question: "Quelle est ta cible ? Décris tout ce que tu sais actuellement de ta cible (BtoB (des professionnels), BtoC (des particuliers), Profil (salariés, indépendants etc)).",
    placeholder: "Ex: BtoB, entrepreneurs indépendants dans le secteur du bien-être...",
    type: "textarea"
  },
  {
    question: "Pourquoi tes clients sont-ils venus te voir ? Ont-ils acheté ton produit ? suivi ton coaching ?",
    placeholder: "Ex: Ils cherchaient à augmenter leur visibilité en ligne...",
    type: "textarea"
  },
  {
    question: "Comment étaient-ils avant et quelles transformations/expérience ont-ils vécu / obtenu après ?",
    placeholder: "Ex: Avant ils avaient peu de clients, après ils ont doublé leur chiffre d'affaires...",
    type: "textarea"
  },
  {
    question: "Quel est le problème majeur de tes clients dont ils sont conscients ? (Tu peux en donner plusieurs mais pas plus de 3)",
    placeholder: "Ex: Manque de visibilité, difficulté à convertir, budget limité...",
    type: "textarea"
  },
  {
    question: "Avez-vous déjà des retours clients ? Si oui, pouvez-vous me les donner ?",
    placeholder: "Ex: \"Grâce à cette méthode, j'ai multiplié mes ventes par 3\" - Jean D.",
    type: "textarea"
  },
  {
    question: "Sur quels réseaux sociaux publiez-vous ?",
    placeholder: "Ex: LinkedIn, Instagram, Facebook...",
    type: "text"
  },
  {
    question: "Expliquez-moi en détail votre offre actuelle ?",
    placeholder: "Ex: Je propose un programme de 3 mois comprenant...",
    type: "textarea"
  }
];

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState(Array(questions.length).fill(''));
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer le study_id passé depuis FreeTrial
  const studyIdFromState = location.state?.studyId;

  const progress = ((currentStep + 1) / questions.length) * 100;

  const handleNext = () => {
    if (!responses[currentStep].trim()) {
      toast.error('Veuillez répondre à la question');
      return;
    }
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!responses[currentStep].trim()) {
      toast.error('Veuillez répondre à la question');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const formattedResponses = questions.map((q, i) => ({
        question_index: i,
        question: q.question,
        answer: responses[i]
      }));

      const response = await axios.post(
        `${API}/api/onboarding/save`,
        { 
          responses: formattedResponses,
          study_id: studyIdFromState  // Passer l'ID de l'étude créée par free-trial
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Étude créée avec succès !');
      navigate(`/study/${response.data.study_id}`);
    } catch (error) {
      console.error('Onboarding error:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentStep];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-3xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-muted font-mono" data-testid="progress-text">
              Étape {currentStep + 1} / {questions.length}
            </span>
            <span className="text-sm text-primary font-semibold" data-testid="progress-percentage">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress value={progress} className="h-2" data-testid="progress-bar" />
        </div>

        {/* Question Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="bg-card border border-border rounded-lg p-8 md:p-12"
        >
          <h2 className="text-2xl md:text-3xl font-heading font-semibold mb-8" data-testid="question-title">
            {currentQuestion.question}
          </h2>

          <div className="mb-8">
            {currentQuestion.type === 'textarea' ? (
              <Textarea
                value={responses[currentStep]}
                onChange={(e) => {
                  const newResponses = [...responses];
                  newResponses[currentStep] = e.target.value;
                  setResponses(newResponses);
                }}
                placeholder={currentQuestion.placeholder}
                className="min-h-[150px] text-lg"
                data-testid="question-textarea"
              />
            ) : (
              <Input
                value={responses[currentStep]}
                onChange={(e) => {
                  const newResponses = [...responses];
                  newResponses[currentStep] = e.target.value;
                  setResponses(newResponses);
                }}
                placeholder={currentQuestion.placeholder}
                className="text-lg"
                data-testid="question-input"
              />
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0}
              data-testid="back-button"
            >
              Précédent
            </Button>

            {currentStep === questions.length - 1 ? (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="shadow-glow-md"
                data-testid="submit-button"
              >
                {loading ? 'Création...' : 'Lancer l\'analyse'}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className="shadow-glow-md"
                data-testid="next-button"
              >
                Suivant
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Onboarding;